import type { User } from '@/types/user';
import { logError } from '@/utils/error-handling';
import { supabase } from '../supabase';
import { CACHE_TTL, getCacheKey, supabaseCache } from './cache';
import { isDuplicateError, safeSupabaseOperation } from './error-handler';

// Mapear de snake_case (DB) a camelCase (TypeScript)
function mapProfileFromDB(dbProfile: any): User {
  return {
    id: dbProfile.id,
    email: dbProfile.email,
    name: dbProfile.name,
    avatar: dbProfile.avatar || '',
    role: dbProfile.role || 'user',
    createdAt: dbProfile.created_at || new Date().toISOString(),
    updatedAt: dbProfile.updated_at,
    lastLogin: dbProfile.last_login,
    firstName: dbProfile.first_name,
    lastName: dbProfile.last_name,
    phone: dbProfile.phone,
    birthDate: dbProfile.birth_date,
    city: dbProfile.city,
    company: dbProfile.company,
    interests: dbProfile.interests || [],
    biography: dbProfile.biography,
    socialMedia: dbProfile.social_media || {},
    hasCompletedOnboarding: dbProfile.has_completed_onboarding || false,
    onboardingRole: dbProfile.onboarding_role,
    onboardingInterests: dbProfile.onboarding_interests || [],
    onboardingGoals: dbProfile.onboarding_goals || [],
    onboardingExperienceLevel: dbProfile.onboarding_experience_level,
    onboardingMatchingPreferences: dbProfile.onboarding_matching_preferences || [],
    networkingSeeking: dbProfile.networking_seeking || [],
    networkingOffering: dbProfile.networking_offering || [],
    professionalKeywords: dbProfile.professional_keywords || [],
    bioShort: dbProfile.bio_short,
    eventObjective: dbProfile.event_objective,
    networkingLevel: dbProfile.networking_level ?? 1,
  };
}

// Mapear de camelCase (TypeScript) a snake_case (DB)
function mapProfileToDB(profile: Partial<User>): any {
  const dbProfile: any = {};
  
  if (profile.name !== undefined) dbProfile.name = profile.name;
  if (profile.avatar !== undefined) dbProfile.avatar = profile.avatar || null;
  if (profile.role !== undefined) dbProfile.role = profile.role;
  
  // Campos opcionales: convertir strings vacíos a null
  if (profile.firstName !== undefined) {
    dbProfile.first_name = profile.firstName && profile.firstName.trim() ? profile.firstName.trim() : null;
  }
  if (profile.lastName !== undefined) {
    dbProfile.last_name = profile.lastName && profile.lastName.trim() ? profile.lastName.trim() : null;
  }
  if (profile.phone !== undefined) {
    dbProfile.phone = profile.phone && profile.phone.trim() ? profile.phone.trim() : null;
  }
  if (profile.birthDate !== undefined) {
    dbProfile.birth_date = profile.birthDate && profile.birthDate.trim() ? profile.birthDate.trim() : null;
  }
  if (profile.city !== undefined) {
    dbProfile.city = profile.city && profile.city.trim() ? profile.city.trim() : null;
  }
  if (profile.company !== undefined) {
    dbProfile.company = profile.company && profile.company.trim() ? profile.company.trim() : null;
  }
  if (profile.biography !== undefined) {
    dbProfile.biography = profile.biography && profile.biography.trim() ? profile.biography.trim() : null;
  }
  if (profile.interests !== undefined) {
    dbProfile.interests = Array.isArray(profile.interests) ? profile.interests : [];
  }
  
  // Social media: solo incluir si hay al menos una red con valor
  if (profile.socialMedia !== undefined) {
    const socialMedia: any = {};
    let hasValue = false;
    Object.entries(profile.socialMedia).forEach(([key, value]) => {
      if (value && value.trim()) {
        socialMedia[key] = value.trim();
        hasValue = true;
      }
    });
    dbProfile.social_media = hasValue ? socialMedia : null;
  }
  
  if (profile.hasCompletedOnboarding !== undefined) dbProfile.has_completed_onboarding = profile.hasCompletedOnboarding;
  if (profile.onboardingRole !== undefined) {
    dbProfile.onboarding_role = profile.onboardingRole && profile.onboardingRole.trim() ? profile.onboardingRole.trim() : null;
  }
  if (profile.onboardingInterests !== undefined) {
    dbProfile.onboarding_interests = Array.isArray(profile.onboardingInterests) ? profile.onboardingInterests : [];
  }
  if (profile.onboardingGoals !== undefined) {
    dbProfile.onboarding_goals = Array.isArray(profile.onboardingGoals) ? profile.onboardingGoals : [];
  }
  if (profile.onboardingExperienceLevel !== undefined) {
    dbProfile.onboarding_experience_level = profile.onboardingExperienceLevel && profile.onboardingExperienceLevel.trim() ? profile.onboardingExperienceLevel.trim() : null;
  }
  if (profile.onboardingMatchingPreferences !== undefined) {
    dbProfile.onboarding_matching_preferences = Array.isArray(profile.onboardingMatchingPreferences) ? profile.onboardingMatchingPreferences : [];
  }
  if (profile.networkingSeeking !== undefined) {
    dbProfile.networking_seeking = Array.isArray(profile.networkingSeeking) ? profile.networkingSeeking : [];
  }
  if (profile.networkingOffering !== undefined) {
    dbProfile.networking_offering = Array.isArray(profile.networkingOffering) ? profile.networkingOffering : [];
  }
  if (profile.professionalKeywords !== undefined) {
    dbProfile.professional_keywords = Array.isArray(profile.professionalKeywords) ? profile.professionalKeywords : [];
  }
  if (profile.bioShort !== undefined) {
    dbProfile.bio_short = profile.bioShort && profile.bioShort.trim() ? profile.bioShort.trim().slice(0, 200) : null;
  }
  if (profile.eventObjective !== undefined) {
    dbProfile.event_objective = profile.eventObjective && profile.eventObjective.trim() ? profile.eventObjective.trim() : null;
  }
  if (profile.networkingLevel !== undefined) {
    dbProfile.networking_level = profile.networkingLevel;
  }

  return dbProfile;
}

export const profilesService = {
  getAllUsers: async (): Promise<User[]> => {
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .select('id, email, name, avatar, role, first_name, last_name, phone, birth_date, city, company, biography, interests, social_media, has_completed_onboarding, onboarding_role, onboarding_interests, onboarding_goals, onboarding_experience_level, onboarding_matching_preferences, networking_seeking, networking_offering, professional_keywords, bio_short, event_objective, networking_level, created_at, updated_at, last_login')
        .order('created_at', { ascending: false }),
      'Error al obtener usuarios'
    );
    
    if (result.error || !result.data) {
      return [];
    }
    
    return result.data.map(mapProfileFromDB);
  },

  getUserById: async (id: string): Promise<User | null> => {
    const cacheKey = getCacheKey('user_by_id', id);
    const cached = supabaseCache.get<User | null>(cacheKey);
    if (cached !== null) return cached; // null es un valor válido, así que verificamos explícitamente

    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .select('id, email, name, avatar, role, first_name, last_name, phone, birth_date, city, company, biography, interests, social_media, has_completed_onboarding, onboarding_role, onboarding_interests, onboarding_goals, onboarding_experience_level, onboarding_matching_preferences, networking_seeking, networking_offering, professional_keywords, bio_short, event_objective, networking_level, created_at, updated_at, last_login')
        .eq('id', id)
        .maybeSingle(),
      'Error al obtener usuario'
    );
    
    if (result.error || !result.data) {
      supabaseCache.set(cacheKey, null, CACHE_TTL.USER); // 2 minutos para nulls (evitar búsquedas repetidas)
      return null;
    }
    
    const user = mapProfileFromDB(result.data);
    supabaseCache.set(cacheKey, user, CACHE_TTL.USER); // 2 minutos - perfiles cambian poco
    return user;
  },

  // Nueva función para obtener múltiples usuarios por IDs en batch
  getUsersByIds: async (ids: string[]): Promise<User[]> => {
    if (ids.length === 0) return [];
    
    // Verificar caché para cada ID
    const uncachedIds: string[] = [];
    const cachedUsers: User[] = [];
    
    for (const id of ids) {
      const cacheKey = getCacheKey('user_by_id', id);
      const cached = supabaseCache.get<User | null>(cacheKey);
      if (cached !== null && cached !== undefined) {
        if (cached) cachedUsers.push(cached);
      } else {
        uncachedIds.push(id);
      }
    }
    
    // Si todos están en caché, retornar
    if (uncachedIds.length === 0) {
      return cachedUsers;
    }
    
    // Cargar los que no están en caché
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .select('id, email, name, avatar, role, first_name, last_name, phone, birth_date, city, company, biography, interests, social_media, has_completed_onboarding, onboarding_role, onboarding_interests, onboarding_goals, onboarding_experience_level, onboarding_matching_preferences, networking_seeking, networking_offering, professional_keywords, bio_short, event_objective, networking_level, created_at, updated_at, last_login')
        .in('id', uncachedIds),
      'Error al obtener usuarios por IDs'
    );
    
    if (result.error || !result.data) {
      return cachedUsers;
    }
    
    const loadedUsers = result.data.map(mapProfileFromDB);
    
    // Guardar en caché
    for (const user of loadedUsers) {
      const cacheKey = getCacheKey('user_by_id', user.id);
      supabaseCache.set(cacheKey, user, CACHE_TTL.USER); // 2 minutos - perfiles cambian poco
    }
    
    return [...cachedUsers, ...loadedUsers];
  },

  getUserByEmail: async (email: string): Promise<User | null> => {
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .select('id, email, name, avatar, role, first_name, last_name, phone, birth_date, city, company, biography, interests, social_media, has_completed_onboarding, onboarding_role, onboarding_interests, onboarding_goals, onboarding_experience_level, onboarding_matching_preferences, networking_seeking, networking_offering, professional_keywords, bio_short, event_objective, networking_level, created_at, updated_at, last_login')
        .eq('email', email.toLowerCase())
        .maybeSingle(),
      'Error al obtener usuario por email'
    );
    
    if (result.error || !result.data) {
      return null;
    }
    
    return mapProfileFromDB(result.data);
  },

  getUsersLimited: async (limit: number = 10): Promise<User[]> => {
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .select('id, email, name, avatar, role, first_name, last_name, phone, birth_date, city, company, biography, interests, social_media, has_completed_onboarding, onboarding_role, onboarding_interests, onboarding_goals, onboarding_experience_level, onboarding_matching_preferences, networking_seeking, networking_offering, professional_keywords, bio_short, event_objective, networking_level, created_at, updated_at, last_login')
        .order('created_at', { ascending: false })
        .limit(limit),
      'Error al obtener usuarios limitados'
    );
    
    if (result.error || !result.data) {
      return [];
    }
    
    return result.data.map(mapProfileFromDB);
  },

  getUsersByRole: async (role: 'admin' | 'user'): Promise<User[]> => {
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .select('id, email, name, avatar, role, first_name, last_name, phone, birth_date, city, company, biography, interests, social_media, has_completed_onboarding, onboarding_role, onboarding_interests, onboarding_goals, onboarding_experience_level, onboarding_matching_preferences, networking_seeking, networking_offering, professional_keywords, bio_short, event_objective, networking_level, created_at, updated_at, last_login')
        .eq('role', role)
        .order('created_at', { ascending: false }),
      'Error al obtener usuarios por rol'
    );
    
    if (result.error || !result.data) {
      return [];
    }
    
    return result.data.map(mapProfileFromDB);
  },

  getUsersByRecent: async (limit?: number): Promise<User[]> => {
    let query = supabase
      .from('profiles')
      .select('id, email, name, avatar, role, first_name, last_name, phone, birth_date, city, company, biography, interests, social_media, has_completed_onboarding, onboarding_role, onboarding_interests, onboarding_goals, onboarding_experience_level, onboarding_matching_preferences, networking_seeking, networking_offering, professional_keywords, bio_short, event_objective, networking_level, created_at, updated_at, last_login')
      .order('created_at', { ascending: false });
    
    if (limit) {
      query = query.limit(limit);
    }
    
    const result = await safeSupabaseOperation(
      () => query,
      'Error al obtener usuarios recientes'
    );
    
    if (result.error || !result.data) {
      return [];
    }
    
    return result.data.map(mapProfileFromDB);
  },

  getUsersByLastLogin: async (limit?: number): Promise<User[]> => {
    let query = supabase
      .from('profiles')
      .select('id, email, name, avatar, role, first_name, last_name, phone, birth_date, city, company, biography, interests, social_media, has_completed_onboarding, onboarding_role, onboarding_interests, onboarding_goals, onboarding_experience_level, onboarding_matching_preferences, networking_seeking, networking_offering, professional_keywords, bio_short, event_objective, networking_level, created_at, updated_at, last_login')
      .order('last_login', { ascending: false, nullsFirst: false });
    
    if (limit) {
      query = query.limit(limit);
    }
    
    const result = await safeSupabaseOperation(
      () => query,
      'Error al obtener usuarios por último login'
    );
    
    if (result.error || !result.data) {
      return [];
    }
    
    return result.data.map(mapProfileFromDB);
  },

  getUserCount: async (): Promise<number> => {
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true }),
      'Error al contar usuarios'
    );
    
    if (result.error || !result.data) {
      return 0;
    }
    
    return (result.data as any).count || 0;
  },

  getUserCountByRole: async (role: 'admin' | 'user'): Promise<number> => {
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('role', role),
      'Error al contar usuarios por rol'
    );
    
    if (result.error || !result.data) {
      return 0;
    }
    
    return (result.data as any).count || 0;
  },

  updateProfile: async (userId: string, profileData: Partial<User>): Promise<boolean> => {
    const dbProfile = mapProfileToDB(profileData);
    
    // Verificar si el perfil existe
    const existing = await profilesService.getUserById(userId);
    
    let result;
    if (existing) {
      // Si existe, hacer UPDATE
      result = await safeSupabaseOperation(
        () => supabase
          .from('profiles')
          .update(dbProfile)
          .eq('id', userId),
        'Error al actualizar perfil'
      );
    } else {
      // Si no existe, crear el perfil con los datos proporcionados
      // Obtener email del usuario autenticado
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        logError(userError, 'ProfilesService.updateProfile.getUser', true);
        return false;
      }
      
      const newProfile = {
        id: userId,
        email: user.email || '',
        name: dbProfile.name || user.email?.split('@')[0] || 'Usuario',
        role: 'user',
        ...dbProfile,
      };
      
      result = await safeSupabaseOperation(
        () => supabase
          .from('profiles')
          .insert(newProfile),
        'Error al crear perfil'
      );
    }
    
    if (result.error) {
      logError(result.error, 'ProfilesService.updateProfile', true);
      logError(
        {
          message: result.error.message,
          details: result.error.details,
          hint: result.error.hint,
          code: result.error.code,
        },
        'ProfilesService.updateProfile.details',
        true
      );
    } else {
      // Invalidar caché del perfil
      supabaseCache.invalidate(getCacheKey('user_by_id', userId));
      supabaseCache.invalidatePattern(`users_by_ids:.*`);
    }
    
    return !result.error;
  },

  updateLastLogin: async (userId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', userId),
      'Error al actualizar último login'
    );
    
    return !result.error;
  },

  createProfile: async (
    userId: string,
    email: string,
    name: string,
    avatar?: string
  ): Promise<boolean> => {
    // Verificar primero si ya existe
    const existing = await profilesService.getUserById(userId);
    if (existing) {
      return true;
    }

    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .insert({
          id: userId,
          email: email,
          name: name,
          avatar: avatar || '',
          role: 'user',
        }),
      'Error al crear perfil'
    );
    
    // Si es error de duplicado, el perfil ya existe (race condition)
    if (result.error && isDuplicateError(result.error)) {
      return true;
    }
    
    return !result.error;
  },

  deleteProfile: async (userId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () => supabase
        .from('profiles')
        .delete()
        .eq('id', userId),
      'Error al eliminar perfil'
    );
    
    if (!result.error) {
      // Invalidar caché del perfil
      supabaseCache.invalidate(getCacheKey('user_by_id', userId));
      supabaseCache.invalidatePattern(`users_by_ids:.*`);
    }
    
    return !result.error;
  },
};
